// utilies/other/QuickSearch.js
import * as Cesium from "cesium";

export function createEntitySearchHighlighter(viewer) {
  if (!viewer || !Cesium) throw new Error("viewer Cesium ");
  const HL_COLOR = Cesium.Color.LIME;

  const index = new Map(); // id(string) -> Set<Entity>
  const orig = new Map(); // Entity -> { apply() }
  const entityToDs = new WeakMap(); // Entity -> DataSource
  const disposers = [];
  const untrackMap = new WeakMap();

  function gv(prop) {
    // getValue helper
    if (!prop) return undefined;
    if (typeof prop.getValue === "function") return prop.getValue(viewer.clock.currentTime);
    return prop;
  }

  function readIds(entity) {
    const out = new Set();
    const props = entity.properties;
    if (!props) return out;

    const bag = gv(props) || {};

    const id1 = gv(props.id) ?? bag.id;
    if (id1 != null && String(id1).trim()) out.add(String(id1).trim());

    const rel = gv(props.relation) ?? bag.relation;
    const relBag = gv(rel) || rel;
    const id2 = relBag && (gv(relBag.id) ?? relBag.id);
    if (id2 != null && String(id2).trim()) out.add(String(id2).trim());

    let rels = gv(props.Relations) ?? bag.Relations;
    rels = gv(rels) || rels;
    if (Array.isArray(rels)) {
      for (const r of rels) {
        const rr = gv(r) || r;
        const rid = rr && (gv(rr.id) ?? rr.id);
        if (rid != null && String(rid).trim()) out.add(String(rid).trim());
      }
    }

    return out;
  }

  function addEntityToIndex(entity) {
    try {
      const ids = readIds(entity);
      if (!ids || ids.size === 0) return;
      for (const k of ids) {
        if (!index.has(k)) index.set(k, new Set());
        index.get(k).add(entity);
      }
    } catch (_) {}
  }

  function removeEntityFromIndex(entity) {
    try {
      entityToDs.delete(entity);
    } catch (_) {}

    for (const [k, set] of index) {
      if (set.delete(entity) && set.size === 0) index.delete(k);
    }
    if (orig.has(entity)) {
      try {
        orig.get(entity).apply();
      } finally {
        orig.delete(entity);
      }
    }
  }

  function trackCollection(entityCollection, ds) {
    if (!entityCollection) return;
    if (untrackMap.has(entityCollection)) return;

    entityCollection.values.forEach((e) => {
      try {
        if (ds) entityToDs.set(e, ds);
      } catch (_) {}
      addEntityToIndex(e);
    });

    const onChanged = (_c, added, removed) => {
      added.forEach((e) => {
        try {
          if (ds) entityToDs.set(e, ds);
        } catch (_) {}
        addEntityToIndex(e);
      });
      removed.forEach(removeEntityFromIndex);
    };

    entityCollection.collectionChanged.addEventListener(onChanged);

    const untrack = () => {
      try {
        entityCollection.collectionChanged.removeEventListener(onChanged);
      } catch (_) {}
    };

    untrackMap.set(entityCollection, untrack);
    disposers.push(untrack);
  }

  // main collections
  trackCollection(viewer.entities, null);

  // existing dataSources
  const dsArr = viewer.dataSources && viewer.dataSources._dataSources;
  if (Array.isArray(dsArr)) dsArr.forEach((ds) => trackCollection(ds.entities, ds));

  // future dataSources
  const onDsAdded = (_c, ds) => trackCollection(ds.entities, ds);
  const onDsRemoved = (_c, ds) => {
    const ec = ds?.entities;
    const untrack = ec && untrackMap.get(ec);
    if (untrack) {
      untrack();
      untrackMap.delete(ec);
    }
    if (ec) ec.values.forEach(removeEntityFromIndex);
  };

  viewer.dataSources.dataSourceAdded.addEventListener(onDsAdded);
  viewer.dataSources.dataSourceRemoved.addEventListener(onDsRemoved);
  disposers.push(() => {
    viewer.dataSources.dataSourceAdded.removeEventListener(onDsAdded);
    viewer.dataSources.dataSourceRemoved.removeEventListener(onDsRemoved);
  });

  function highlightEntity(entity) {
    if (orig.has(entity)) return;
    const restorers = [];

    if (entity.point) {
      const prev = entity.point.color;
      restorers.push(() => (entity.point.color = prev));
      entity.point.color = HL_COLOR;
    }

    if (entity.billboard) {
      const prev = entity.billboard.color;
      restorers.push(() => (entity.billboard.color = prev));
      entity.billboard.color = HL_COLOR;
    }

    if (entity.label) {
      const prev = entity.label.fillColor;
      restorers.push(() => (entity.label.fillColor = prev));
      entity.label.fillColor = HL_COLOR;
    }

    if (entity.polyline) {
      const prevMat = entity.polyline.material;
      restorers.push(() => (entity.polyline.material = prevMat));
      entity.polyline.material = new Cesium.ColorMaterialProperty(HL_COLOR);

      const prevWidthProp = entity.polyline.width;
      restorers.push(() => (entity.polyline.width = prevWidthProp));

      const currentWidth = gv(prevWidthProp);
      const baseWidth = typeof currentWidth === "number" && !Number.isNaN(currentWidth) ? currentWidth : 1;
      entity.polyline.width = new Cesium.ConstantProperty(baseWidth + 3);
    }

    if (entity.polygon) {
      const prev = entity.polygon.material;
      restorers.push(() => (entity.polygon.material = prev));
      entity.polygon.material = new Cesium.ColorMaterialProperty(HL_COLOR);
    }

    if (entity.model) {
      const prevColor = entity.model.color;
      const prevBlend = entity.model.colorBlendMode;
      restorers.push(() => {
        entity.model.color = prevColor;
        entity.model.colorBlendMode = prevBlend;
      });
      entity.model.color = HL_COLOR;
      entity.model.colorBlendMode = Cesium.ColorBlendMode.REPLACE;
    }

    if (restorers.length === 0) return;
    orig.set(entity, { apply: () => restorers.forEach((fn) => fn()) });
  }

  function clearAllHighlights() {
    for (const [entity, r] of orig) {
      try {
        r.apply();
      } catch (_) {}
    }
    orig.clear();
  }

  function parseIdsText(idsText) {
    return Array.from(
      new Set(
        String(idsText || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      ),
    );
  }

  function getDisplayTextForEntity(entity, searchId) {
    const name = entity?.name ? String(entity.name) : "";
    const entId = entity?.id ? String(entity.id) : "";
    if (name && entId) return `${name} (${entId})`;
    if (name) return name;
    if (entId) return entId;
    return `Entity matched ${String(searchId)}`;
  }

  function zoomToEntities(entities, options = {}) {
    try {
      const maybePromise = viewer.zoomTo(entities, options.offset);
      return Promise.resolve(maybePromise)
        .then(() => true)
        .catch(() => fallbackFly());
    } catch (_) {
      return fallbackFly();
    }

    function fallbackFly() {
      try {
        const time = viewer.clock.currentTime;
        const points = [];

        for (const ent of entities) {
          if (ent.position) {
            const p = typeof ent.position.getValue === "function" ? ent.position.getValue(time) : ent.position;
            if (p) points.push(p);
          }
        }

        if (points.length === 0) return Promise.resolve(false);

        const bs = Cesium.BoundingSphere.fromPoints(points);
        viewer.camera.flyToBoundingSphere(bs, options);
        return Promise.resolve(true);
      } catch (_) {
        return Promise.resolve(false);
      }
    }
  }

  return {
    findByIdsText(idsText) {
      const ids = parseIdsText(idsText);
      if (ids.length === 0) return { found: 0, matchedIds: [], counts: {}, matches: [] };

      const counts = {};
      ids.forEach((id) => (counts[id] = 0));

      const matchedIds = [];
      const matches = [];
      let found = 0;

      for (const id of ids) {
        const set = index.get(id);
        const ents = set ? Array.from(set) : [];
        const cnt = ents.length;

        counts[id] = cnt;

        if (cnt > 0) {
          matchedIds.push(id);

          for (const ent of ents) {
            highlightEntity(ent);
            matches.push({
              searchId: id,
              entityId: String(ent.id),
              displayText: getDisplayTextForEntity(ent, id),
              layerName: entityToDs.get(ent)?.layerName ?? entityToDs.get(ent)?.name ?? "",
              desc: entityToDs.get(ent) && entityToDs.get(ent).show === false ? "Layer Invisible" : "",
            });
          }

          found += cnt;
        }
      }

      return { found, matchedIds, counts, matches };
    },

    reset() {
      clearAllHighlights();
    },

    dispose() {
      clearAllHighlights();
      disposers.forEach((d) => {
        try {
          d();
        } catch (_) {}
      });
      disposers.length = 0;
      index.clear();
    },

    zoomToId(id, options = {}) {
      const set = index.get(String(id));
      if (!set || set.size === 0) return Promise.resolve(false);
      return zoomToEntities(Array.from(set), options);
    },

    zoomToEntityId(entityId, options = {}) {
      const ent = viewer?.entities?.getById?.(String(entityId));
      if (ent) return zoomToEntities([ent], options);

      const dsArr2 = viewer?.dataSources?._dataSources;
      if (Array.isArray(dsArr2)) {
        for (const ds of dsArr2) {
          const e = ds?.entities?.getById?.(String(entityId));
          if (e) return zoomToEntities([e], options);
        }
      }

      return Promise.resolve(false);
    },
  };
}
